/**
 * =============================================================================
 *  GHI CHU THICH THANH DOI TUONG /Annot THAT
 * =============================================================================
 *
 *  Truoc tep nay, PdfCoreBrowser.apply() TU CHOI ca ba lenh chu thich va save()
 *  khong ghi gi. Chu thich hien tren man hinh, roi bien mat khong dau vet trong
 *  tep xuat ra. Mat du lieu am tham - te hon mot loi tu choi trung thuc.
 *
 * =============================================================================
 *  VI SAO /Annot THAT CHU KHONG PHAI VE DE LEN
 * =============================================================================
 *  Ve mot hinh chu nhat vang vao content stream cua trang cung cho ra mot trang
 *  trong y het. Nhung luc do no khong con la chu thich: Acrobat khong chon duoc,
 *  khong sua mau duoc, khong xoa duoc, khong hien trong bang chu thich, khong
 *  xuat ra duoc file FDF, va trinh doc man hinh khong doc duoc loi ghi chu.
 *  No thanh mot vet muc dinh vao to giay.
 *
 *  Ca diem cua chu thich la no NAM TREN tai lieu chu khong nam TRONG tai lieu.
 *  Nen o day ta dung dictionary /Annot bang API cap thap cua pdf-lib (pdf-lib
 *  khong co API cap cao cho viec nay) va gan vao /Annots cua tung trang.
 *
 * =============================================================================
 *  BA DIEU DE SAI, DA CHOT BANG TEST
 * =============================================================================
 *  1. KHONG /AP = VO HINH o nhieu trinh xem va tren duong in. Moi chu thich ghi
 *     ra deu co /AP - xem appearance.ts.
 *  2. /BBox PHAI BANG /Rect. Neu lech, trinh xem CO GIAN dong hinh dang cho vua
 *     /Rect: net ve tay bi bop meo, mui ten bi cat cut. Nen o day /Rect duoc
 *     LAY TU hop bao cua dong hinh dang chu khong nguoc lai.
 *  3. CHU TIENG VIET phai la chuoi hex UTF-16BE. PDFString.of("Ghi chú") ghi ra
 *     byte Latin-1 va Acrobat doc thanh "Ghi ch?". Moi truong van ban (/Contents,
 *     /T, /Subj) deu di qua PDFHexString.fromText().
 *
 * =============================================================================
 *  TINH TAT DINH
 * =============================================================================
 *  /M va /CreationDate lay tu `annotation.createdAtMs`, KHONG tu Date.now().
 *  Nho vay hai lan luu cach nhau mot phut cho ra hai luong byte giong het nhau -
 *  dieu kien de test chong "ap lenh hai lan" con viet duoc. Cung ly do da khien
 *  pages/operations.ts phai dat updateMetadata: false.
 */
import {
  PDFDocument,
  PDFHexString,
  PDFString,
  type PDFObject,
  type PDFPage,
  type PDFRef,
} from 'pdf-lib';
import type { Annotation, PdfRect } from '@pdfeditor/types';

import type { PagePlan } from '../pages/plan';
import { placeAnnotations } from './placement';
import { buildAppearance, boundsOfPoints, num, type AppearanceOptions } from './appearance';
import {
  fontStyleKey,
  loadBundledFamilyStyle,
  normalizeFontFamilyId,
  getFontFamily,
  type FontStyleKey,
  type FontFamilyId,
} from '../fonts/bundled';
import { rawFontkit } from '../fonts/fontkit';
import type { Font } from '../fonts/fontkit';
import { DEFAULT_TEXT_SIZE_PT, layoutTextAnnotation } from '../../ui/textBoxMetrics';
import { quadPointsOf } from './quadPoints';
import { fillOf, strokeWidthOf } from './shapes';
import type { ImageAsset } from './images';
import {
  DEFAULT_BORDER_WIDTH_PT,
  NOTE_ICON_SIZE_PT,
  checkAnnotation,
  type AnnotationRejection,
} from './support';

/**
 * Kieu gia tri ma PDFContext.obj() nhan duoc.
 *
 * pdf-lib khong xuat `LiteralObject` ra ngoai goi, va suy no tu chu ky ham thi
 * TypeScript chi lay duoc NAP CHONG CUOI CUNG (LiteralArray) - tuc la sai. Nen
 * cho nay go lai dung mot lan, o mot cho, kem ghi chu nay.
 */
type PdfLiteral =
  | PDFObject
  | string
  | number
  | boolean
  | null
  | undefined
  | PdfLiteral[]
  | { [key: string]: PdfLiteral };

/** /Subtype theo dac ta cho tung loai trong hop dong kieu cua ta. */
const SUBTYPE: Partial<Record<Annotation['kind'], string>> = {
  underline: 'Underline',
  strikeout: 'StrikeOut',
  squiggly: 'Squiggly',
  ink: 'Ink',
  square: 'Square',
  circle: 'Circle',
  line: 'Line',
  arrow: 'Line', // Mui ten LA mot /Line, khac nhau o /LE.
  'text-note': 'Text',
  'free-text': 'FreeText',
  // Anh chen vao LA mot /Stamp: dac ta khong co /Subtype rieng cho "anh". Con
  // dau tieu chuan cua Acrobat cung la mot /Stamp voi /AP la anh - ta di dung
  // duong do, nen Acrobat chon / di chuyen / xoa duoc anh nhu moi chu thich.
  stamp: 'Stamp',
};

/** Cac loai dung /QuadPoints (nhom chu thich danh dau van ban). */
const QUAD_KINDS = new Set<Annotation['kind']>([
  'underline',
  'strikeout',
  'squiggly',
]);

/** Co /F: bit 3 (gia tri 4) = In duoc. Khong dat thi chu thich khong len giay. */
const FLAG_PRINT = 4;

export interface AnnotationWriteOptions {
  /**
   * So do trang cua TEP DICH. Bo trong = tep dich giu nguyen thu tu trang goc.
   * Xem placement.ts: day la thu lam cho chu thich di theo trang khi sap xep
   * lai va bien mat khi trang bi xoa.
   */
  plan?: PagePlan;
  /** Ten mac dinh cho /T khi ban than chu thich khong mang. */
  author?: string;
  /** Be rong net DU PHONG - chi dung khi chu thich khong mang gia tri rieng. */
  lineWidthPt?: number;
  /**
   * Byte cua cac anh da ghi nhan, tra cuu theo `Annotation.imageRef`.
   *
   * Truyen vao chu khong doc tu mot bien toan cuc: writeAnnotations() la ham
   * thuan tuy tren dau vao cua no, va do la dieu kien de "luu hai lan cho ra hai
   * tep giong het nhau" con dung.
   */
  images?: ReadonlyMap<string, ImageAsset>;
}

export interface AnnotationWriteReport {
  written: number;
  /** Chi so trang TEP DICH -> so chu thich da gan. */
  perPage: Map<number, number>;
  /** Bi loai, kem ly do doc duoc. KHONG BAO GIO im lang. */
  skipped: { annotation: Annotation; reason: string }[];
  /** Bi loai vi trang cua no khong con trong tep dich. */
  droppedWithPage: Annotation[];
}

export interface AnnotationWriteResult {
  bytes: Uint8Array;
  report: AnnotationWriteReport;
}

/**
 * Hinh chu nhat THUC SU dung cho chu thich, sau khi chuan hoa.
 *
 * Hai loai khong dung thang `annotation.rect`:
 *   - ghi chu dinh: giao dien chi biet DIEM nguoi dung bam, con bieu tuong thi
 *     co kich thuoc co dinh. Goc TREN-TRAI cua bieu tuong dat vao diem do.
 *   - ve tay: hinh chu nhat that la hop bao cua cac net, khong phai thu giao
 *     dien doan truoc khi lam muot.
 */
export function normalizedRect(annotation: Annotation, lineWidthPt: number): PdfRect {
  // CHI dang bieu tuong moi bi ep ve o vuong co dinh. Ghi chu dang hop giu hinh
  // chu nhat da DO tu noi dung cua no (xem measuredTextRect): ep no ve 20pt la
  // dung cach bien mot ghi chu doc duoc thanh o vuong vang nho ma nguoi dung
  // phan nan.
  if (annotation.kind === 'text-note' && annotation.noteStyle === 'icon') {
    const size = NOTE_ICON_SIZE_PT;
    const anchorTopY = annotation.rect.y + annotation.rect.height;
    return { x: annotation.rect.x, y: anchorTopY - size, width: size, height: size };
  }
  if (annotation.kind === 'ink') {
    const paths = (annotation.inkPaths ?? []).filter((p) => p.length >= 2);
    if (paths.length > 0) return boundsOfPoints(paths, lineWidthPt);
  }
  return annotation.rect;
}

/** Loai chu thich MANG CHU trong chinh dong hinh dang cua no. */
function carriesText(annotation: Annotation): boolean {
  if (annotation.kind === 'free-text') return true;
  return annotation.kind === 'text-note' && annotation.noteStyle !== 'icon';
}

/**
 * FONT NHUNG cho hop chu, MOT ban cho moi tai lieu.
 *
 * Nhung lai cho tung chu thich se cho ra N ban sao cua cung mot font trong tep -
 * mot tai lieu hai muoi ghi chu se phinh them vai megabyte. WeakMap chu khong
 * phai bien module: hai tai lieu duoc luu song song phai co font rieng, va ban
 * ghi phai bien mat cung tai lieu.
 */
const embeddedFonts = new WeakMap<PDFDocument, Map<string, Promise<EmbeddedTextFont | null>>>();

/** Khoa bo nho dem font nhung: mot cap (ho font, mat chu) trong pham vi mot tai lieu. */
function embeddedFontKey(familyId: FontFamilyId, style: FontStyleKey): string {
  return `${familyId}:${style}`;
}

interface EmbeddedTextFont {
  fontRef: unknown;
  encodeHex(text: string): string;
  /** Doi tuong fontkit - dung de DO, cung ban font voi thu da nhung. */
  metricsFont: Font;
}

/**
 * MAT CHU DA NHUNG cho mot tai lieu, MOT ban cho moi mat chu duoc dung.
 *
 * =============================================================================
 *  CAI GIA THAT, BANG SO
 * =============================================================================
 *  Moi mat chu Noto Sans nhung KHONG subset nang khoang 345KB trong tep ra. Mot
 *  tai lieu chi dung chu thuong: 345KB. Dung ca bon (thuong / dam / nghieng /
 *  dam-nghieng): khoang 1.4MB.
 *
 *  VI SAO KHONG SUBSET: dong hinh dang duoc dung TRUOC khi pdf-lib biet het cac
 *  glyph se dung, va mot subset chot qua som se thieu dung nhung dau tieng Viet
 *  duoc go vao sau cung. Mot vong ghi hai luot (do het chuoi truoc, roi moi
 *  nhung) se sua duoc, nhung no doi cau truc ca duong ghi va chua duoc lam. Cho
 *  toi luc do: "nang" thang "mat dau" moi lan.
 *
 *  VI SAO CHI NHUNG MAT CHU DUOC DUNG: nhung ca bon cho moi tai lieu se bat
 *  nguoi chi go chu thuong tra 1.4MB de doi lay dung 345KB gia tri. Map o day
 *  chi sinh ra ban ghi khi co mot doi tuong THAT SU can mat chu do.
 *
 *  WeakMap chu khong phai bien module: hai tai lieu duoc luu song song phai co
 *  font rieng, va ban ghi phai bien mat cung tai lieu.
 */
async function textFontFor(
  doc: PDFDocument,
  familyId: FontFamilyId,
  style: FontStyleKey,
): Promise<EmbeddedTextFont | null> {
  let perDoc = embeddedFonts.get(doc);
  if (!perDoc) {
    perDoc = new Map();
    embeddedFonts.set(doc, perDoc);
  }
  const key = embeddedFontKey(familyId, style);
  const existing = perDoc.get(key);
  if (existing) return existing;

  const created = (async (): Promise<EmbeddedTextFont | null> => {
    const bundled = await loadBundledFamilyStyle(familyId, style);
    if (!bundled) return null;
    doc.registerFontkit(rawFontkit as Parameters<PDFDocument['registerFontkit']>[0]);
    const embedded = await doc.embedFont(bundled.bytes as Uint8Array, { subset: false });
    return {
      fontRef: embedded.ref,
      encodeHex: (text: string) => embedded.encodeText(text).toString(),
      metricsFont: bundled.font,
    };
  })();

  perDoc.set(key, created);
  return created;
}

/**
 * Gan MOT chu thich vao mot trang. Tra ve tham chieu vua tao, hoac ly do tu choi.
 *
 * Tach rieng khoi writeAnnotations() de bo test goi duoc no tren mot trang don
 * ma khong phai di qua ca duong luu.
 */
export async function attachAnnotation(
  doc: PDFDocument,
  page: PDFPage,
  input: Annotation,
  options: { author?: string; lineWidthPt?: number; images?: ReadonlyMap<string, ImageAsset> } = {},
): Promise<{ ok: true; ref: PDFRef } | ({ ok: false } & AnnotationRejection)> {
  const check = checkAnnotation(input);
  if (!check.ok) return check;

  // Be rong net CUA DOI TUONG, voi tham so cua lan ghi lam gia tri du phong.
  const lineWidthPt = strokeWidthOf(input, options.lineWidthPt ?? DEFAULT_BORDER_WIDTH_PT);
  const subtype = SUBTYPE[input.kind];
  if (!subtype) {
    return {
      ok: false,
      code: 'unsupported-feature',
      reason: `Không có /Subtype cho loại chú thích "${input.kind}".`,
    };
  }

  // Chuan hoa TRUOC khi dung dong hinh dang: dong hinh dang ve theo hinh chu
  // nhat da chuan hoa, va /Rect cuoi cung lay tu chinh dong hinh dang do.
  let annotation: Annotation = { ...input, rect: normalizedRect(input, lineWidthPt) };

  // CHU: do bo cuc TRUOC khi dung dong hinh dang, vi hinh chu nhat cua hop la
  // KET QUA cua phep do chu khong phai dau vao cua no. Giao dien chi biet goc
  // tren-trai nguoi dung bam; chieu cao that phu thuoc so dong sau khi ngat va
  // vao muc vuon cua dau tieng Viet.
  let textAppearance: AppearanceOptions['text'];
  if (carriesText(annotation)) {
    // MAT CHU DUNG NGAY TU DAU: `bold` / `italic` cua chinh doi tuong quyet
    // dinh tep font nao duoc nhung VA tep font nao duoc dung de do. Mot ban
    // ghi do bang Regular roi nhung ban Bold la dung kieu lech ma bai hoc so 3
    // noi toi, chi la lech ben trong cung mot ham.
    // HO FONT DUNG NGAY TU DAU, cung ly do voi `bold`/`italic`: `fontFamily` cua
    // chinh doi tuong quyet dinh tep font nao duoc nhung VA tep font nao duoc
    // dung de do. Do bang mot ho roi nhung bang ho khac la kieu lech ma bai hoc
    // so 3 noi toi. Ma khong hop le (hoac vang) lui ve ho mac dinh.
    const familyId = normalizeFontFamilyId(annotation.fontFamily);
    const styleKey = fontStyleKey(annotation);
    const font = await textFontFor(doc, familyId, styleKey);
    if (!font) {
      const familyLabel = getFontFamily(familyId).label;
      const faceLabel =
        styleKey === 'regular'
          ? familyLabel
          : `${familyLabel} (${styleKey === 'bold' ? 'đậm' : styleKey === 'italic' ? 'nghiêng' : 'đậm nghiêng'})`;
      return {
        ok: false,
        code: 'io-error',
        reason:
          `Không nạp được font đóng gói (${faceLabel}). Hộp chữ và ghi chú dạng hộp cần font này ` +
          'để in ra đúng dấu tiếng Việt, nên chúng không được ghi ra thay vì ghi ra mất dấu.',
      };
    }

    const fontSizePt = annotation.fontSizePt ?? DEFAULT_TEXT_SIZE_PT;
    // CUNG ham ma lop hien thi goi. Xem layoutTextAnnotation() - hai ben tu tinh
    // lay la duong dan thang toi mot ban xem truoc noi doi.
    //
    // `font.metricsFont` la MAT CHU DA NHUNG, khong phai ban Regular: truyen mot
    // Font tran vao day nghia la "mat chu da giai xong o tren", nen so do va tep
    // ra khong the lech nhau.
    const { rect, metrics } = layoutTextAnnotation(font.metricsFont, annotation);

    // DO PHU GLYPH PHAI HOI DUNG MAT CHU SE DUOC GHI. Ca bon mat chu Noto Sans
    // deu du bang chu cai tieng Viet, nhung cau tra loi do phai den tu chuong
    // trinh font that cua mat chu do - khong phai tu ban Regular, va khong phai
    // tu mot gia dinh.
    if (!metrics.coverage.ok) {
      return {
        ok: false,
        code: 'font-not-editable',
        reason:
          `Font đóng gói không có glyph cho một số ký tự trong nội dung ` +
          `(“${metrics.coverage.missing.join('')}”). Ghi ra sẽ thành ô vuông trống trên trang.`,
      };
    }

    annotation = { ...annotation, rect };
    textAppearance = {
      fontRef: font.fontRef,
      encodeHex: font.encodeHex,
      lines: metrics.placedLines,
      fontSizePt,
    };
  }

  // ANH: nhung TRUOC khi dung dong hinh dang, vi dong hinh dang can tham chieu
  // toi doi tuong anh. Loi nhung o day la loi that (tep hong, dinh dang la) va
  // phai thanh mot ly do doc duoc, khong duoc thanh mot ngoai le nem len tren.
  let imageRef: unknown;
  if (annotation.kind === 'stamp') {
    const asset = annotation.imageRef ? options.images?.get(annotation.imageRef) : undefined;
    if (!asset) {
      return {
        ok: false,
        code: 'io-error',
        reason:
          `Không còn dữ liệu của ảnh “${annotation.imageRef ?? '?'}”. Ảnh chỉ được giữ trong ` +
          'bộ nhớ của phiên làm việc; nếu trang đã được tải lại thì cần chọn lại tệp ảnh.',
      };
    }
    try {
      const embedded =
        asset.format === 'png'
          ? await doc.embedPng(asset.bytes)
          : await doc.embedJpg(asset.bytes);
      imageRef = embedded.ref;
    } catch (err) {
      return {
        ok: false,
        code: 'engine-panic',
        reason: `Không nhúng được ảnh “${asset.label}” vào tệp: ${String(err).slice(0, 120)}`,
      };
    }
  }

  const appearance = buildAppearance(annotation, { lineWidthPt, imageRef, text: textAppearance });
  if (!appearance) {
    return {
      ok: false,
      code: 'unsupported-feature',
      reason: `Không dựng được dòng hình dạng cho loại "${input.kind}".`,
    };
  }

  // DIEM MAU CHOT: /Rect = hop bao cua dong hinh dang. Hai gia tri nay lech
  // nhau thi trinh xem co gian dong hinh dang cho vua /Rect, va net ve tay bi
  // bop meo mot cach kho hieu. Chung duoc lay tu MOT phep tinh.
  const rect = appearance.bbox;
  const context = doc.context;

  const apRef = context.register(
    context.stream(appearance.content, {
      Type: 'XObject',
      Subtype: 'Form',
      FormType: 1,
      BBox: [rect.x, rect.y, rect.x + rect.width, rect.y + rect.height],
      Matrix: [1, 0, 0, 1, 0, 0],
      // Nhom trong suot: can de /CA (do mo) cua chu thich duoc ap len TOAN BO
      // dong hinh dang nhu mot lop, thay vi len tung net rieng le. Khac biet lo
      // ra ngay khi mot hinh vua co vien vua co to: khong co nhom nay, cho vien
      // chong len mien to se dam mau hon phan con lai.
      Group: { Type: 'Group', S: 'Transparency', CS: 'DeviceRGB' },
      Resources: appearance.resources as PdfLiteral,
    }),
  );

  const date = PDFString.fromDate(new Date(annotation.createdAtMs));
  const dict: Record<string, PdfLiteral> = {
    Type: 'Annot',
    Subtype: subtype,
    Rect: [rect.x, rect.y, rect.x + rect.width, rect.y + rect.height],
    // /NM la dinh danh duy nhat trong pham vi trang. Dung chinh ma cua ta o day
    // la thu lam cho mot vong "ghi ra roi doc lai" doi chieu duoc tung doi tuong,
    // va la thu duong annotation.update / annotation.remove sau nay bam vao.
    NM: PDFString.of(annotation.id),
    F: FLAG_PRINT,
    C: [annotation.colorRgb[0], annotation.colorRgb[1], annotation.colorRgb[2]],
    CA: annotation.opacity,
    M: date,
    CreationDate: date,
    AP: { N: apRef },
  };

  // Chu tieng Viet: hex UTF-16BE, khong bao gio PDFString.of. Xem dau tep.
  if (annotation.contents !== undefined && annotation.contents !== '') {
    dict['Contents'] = PDFHexString.fromText(annotation.contents);
  } else {
    // /Contents rong van phai co mat: nhieu trinh xem dung no lam noi dung o
    // chu giai, va thieu khoa nay thi o chu giai khong mo ra duoc.
    dict['Contents'] = PDFHexString.fromText('');
  }
  const author = annotation.author ?? options.author;
  if (author) dict['T'] = PDFHexString.fromText(author);

  if (QUAD_KINDS.has(annotation.kind)) {
    dict['QuadPoints'] = quadPointsOf(rect);
  }

  if (annotation.kind === 'ink') {
    const paths = (annotation.inkPaths ?? []).filter((p) => p.length >= 2);
    dict['InkList'] = paths.map((path) => path.flatMap((p) => [p.x, p.y]));
    dict['BS'] = { Type: 'Border', W: lineWidthPt, S: 'S' };
  }

  if (annotation.kind === 'square' || annotation.kind === 'circle') {
    dict['BS'] = { Type: 'Border', W: lineWidthPt, S: 'S' };
    // /IC = mau to BEN TRONG. Khong to thi KHONG duoc ghi khoa nay - ghi mot
    // mang rong hay mau trang deu bi mot so trinh xem hieu thanh "to trang",
    // va luc do hinh se che kin chu nam duoi no.
    const fill = fillOf(annotation);
    if (fill) dict['IC'] = [fill[0], fill[1], fill[2]];
  }

  if (annotation.kind === 'line' || annotation.kind === 'arrow') {
    // /L la hai dau mut THAT, khong phai hop bao. Chung khac nhau vi /Rect da
    // duoc noi rong de chua dau mui ten.
    const from = { x: input.rect.x, y: input.rect.y };
    const to = { x: input.rect.x + input.rect.width, y: input.rect.y + input.rect.height };
    dict['L'] = [from.x, from.y, to.x, to.y];
    dict['BS'] = { Type: 'Border', W: lineWidthPt, S: 'S' };
    dict['LE'] = annotation.kind === 'arrow' ? ['None', 'OpenArrow'] : ['None', 'None'];
  }

  if (annotation.kind === 'free-text') {
    // KHONG VIEN, noi ra trong CA HAI cho.
    //
    // Dong hinh dang da khong ve vien (textBoxAppearance), nhung tu dien chu
    // thich con mot duong thu hai: khoa /Border mac dinh theo dac ta la
    // [0 0 1] - be day 1 - va mot trinh xem tu dung lai hinh dang cua /FreeText
    // (Acrobat lam viec nay khi nguoi dung bam vao sua) se ke lai dung cai vien
    // ta vua bo. Ghi ro be day 0 o ca /BS lan /Border thi khong con duong nao.
    dict['BS'] = { Type: 'Border', W: 0, S: 'S' };
    dict['Border'] = [0, 0, 0];
  }

  if (annotation.kind === 'text-note') {
    dict['Name'] = 'Comment';
    // Mo san mot o chu giai khi vua mo tai lieu la mot cach lam phien. Acrobat
    // mac dinh dong, ta theo.
    dict['Open'] = false;
  }

  const ref = context.register(context.obj(dict));
  page.node.addAnnot(ref);
  return { ok: true, ref };
}

/**
 * Ghi ca danh sach chu thich vao mot luong byte PDF.
 *
 * `bytes` KHONG bi sua: pdf-lib doc mot ban sao va ta tra ve luong byte moi.
 * Day la ADR-003 - byte goc bat bien - va no dung ca o day.
 *
 * Ham nay CHAY SAU khi so do trang da duoc vat chat hoa. Ly do: chu thich duoc
 * gan theo chi so trang cua TEP DICH, ma chi so do chi ton tai sau khi trang da
 * duoc sao chep va sap xep xong. Gan truoc roi de copyPages mang di se nhan doi
 * chu thich khi mot trang xuat hien hai lan trong so do.
 */
export async function writeAnnotations(
  bytes: Uint8Array,
  annotations: readonly Annotation[],
  options: AnnotationWriteOptions = {},
): Promise<AnnotationWriteResult> {
  const report: AnnotationWriteReport = {
    written: 0,
    perPage: new Map(),
    skipped: [],
    droppedWithPage: [],
  };

  if (annotations.length === 0) {
    return { bytes: bytes.slice(), report };
  }

  const { placements, dropped } = placeAnnotations(annotations, options.plan);
  report.droppedWithPage = dropped;

  if (placements.length === 0) {
    return { bytes: bytes.slice(), report };
  }

  const doc = await PDFDocument.load(bytes.slice(), {
    ignoreEncryption: true,
    // Xem ghi chu ve tinh tat dinh o dau tep: de pdf-lib dong dau ModDate moi
    // lan luu la lam hai lan luu khac nhau tung byte.
    updateMetadata: false,
  });
  const pages = doc.getPages();

  for (const placement of placements) {
    const page = pages[placement.targetPageIndex];
    if (!page) {
      report.skipped.push({
        annotation: placement.annotation,
        reason: `Trang ${placement.targetPageIndex + 1} không tồn tại trong tệp kết quả.`,
      });
      continue;
    }

    const attached = await attachAnnotation(doc, page, placement.annotation, {
      author: options.author,
      lineWidthPt: options.lineWidthPt,
      images: options.images,
    });
    if (!attached.ok) {
      report.skipped.push({ annotation: placement.annotation, reason: attached.reason });
      continue;
    }

    report.written += 1;
    report.perPage.set(
      placement.targetPageIndex,
      (report.perPage.get(placement.targetPageIndex) ?? 0) + 1,
    );
  }

  return { bytes: await doc.save({ useObjectStreams: false }), report };
}

// So thuc dang toan tu PDF duoc dung chung voi appearance.ts. Xuat lai o day de
// bo test doi chieu dinh dang so ma khong phai nho hai duong nhap.
export { num };
